// context/SocketContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { useAuth } from './auth-context';

// Import modular feature hooks
import { useServiceRequests } from './socket/use-service-requests';
import { useTracking } from './socket/use-tracking';
import { useScheduledTasks } from './socket/use-scheduled-tasks';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user, authToken } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  useEffect(() => {
    if (user && user._id) {
      const newSocket = io(process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000', {
        transports: ['websocket', 'polling'],
        withCredentials: true,
        auth: {
          token: authToken
        },
        query: {
          userId: user._id,
          role: user.role?.name
        }
      });

      // Connection events
      newSocket.on('connect', () => {
        setIsConnected(true);
        setConnectionStatus('connected');

        // Join appropriate rooms based on user role
        const roleName = user.role?.name;
        if (roleName === 'handyman') {
          newSocket.emit('join-handyman-room', user._id);
        } else if (roleName === 'customer') {
          newSocket.emit('join-customer-room', user._id);
        }

        // Always join user's personal room
        newSocket.emit('join-user-room', user._id);
      });

      newSocket.on('disconnect', (reason) => {
        console.log('❌ Disconnected from server. Reason:', reason);
        setIsConnected(false);
        setConnectionStatus('disconnected');
      });

      newSocket.on('connect_error', (error) => {
        console.error('❌ Connection error:', error);
        setConnectionStatus('error');
      });

      setSocket(newSocket);

      // Cleanup on unmount or user change
      return () => {
        newSocket.close();
      };
    }
  }, [user?._id, user?.role?.name]);

  // Invoke modular sub-hooks to handle state and socket event listeners
  const serviceRequestsModule = useServiceRequests(socket, user);
  const trackingModule = useTracking(socket, user);
  const scheduledTasksModule = useScheduledTasks(socket, user);

  // Combine connection state with modular state and helper functions
  const value = {
    socket,
    isConnected,
    connectionStatus,
    ...serviceRequestsModule,
    ...trackingModule,
    ...scheduledTasksModule,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};